import BlueBtn from "../button/PrimaryBtn";
import GreyBtn from "../button/GreyBtn";
import { useTranslation } from "react-i18next";
import SecondaryBtn from "../button/SecondaryBtn";
import PrimaryBtn from "../button/PrimaryBtn";

interface Lifeline {
  lifeline_id: {
    name: { en: string; ar: string };
    description: { en: string; ar: string };
    image: string;
    _id: string;
  };
  is_used: boolean;
  _id: string;
}

interface LifelineConfirmModalProps {
  isOpen: boolean;
  lifeline: Lifeline | null;
  teamName: string;
  onConfirm: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

const LifelineConfirmModal = ({
  isOpen,
  lifeline,
  teamName,
  onConfirm,
  onClose,
  isLoading = false,
}: LifelineConfirmModalProps) => {
  const currentLanguage = (localStorage.getItem("i18nextLng") || "en") as "en" | "ar";
  const baseUrl = import.meta.env.VITE_BASE_CDN_URL;
  const { t } = useTranslation();

  if (!isOpen || !lifeline) return null;

  const lifelineData = lifeline.lifeline_id;
  const lifelineName = lifelineData?.name?.[currentLanguage] || lifelineData?.name?.en || "Lifeline";
  const lifelineDescription = lifelineData?.description?.[currentLanguage] || lifelineData?.description?.en || "";
  const lifelineImage = lifelineData?.image ? `${baseUrl}/${lifelineData.image}` : "";

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-5 z-50">
      <div
        style={{
          background: "radial-gradient(circle at center, #FFFDF5 0%, #FBE8D3 50%, #F2D1A8 80%)",
        }}
        className="modal 2xl:rounded-[2.083vw] xl:rounded-[2.083vw] lg:rounded-[2.5vw] rounded-3xl 2xl:p-[2vw] xl:p-[2vw] lg:p-[2.5vw] md:p-7 p-4 2xl:max-w-[26.042vw] max-w-[400px] w-full relative animate-[bounceIn_0.4s_ease-out] 2xl:border-[0.2vw] lg:border-[0.25vw] border-3 border-model-border auth-page overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] hide-scroll"
      >
        {/* Close Button */}
        <div className="flex justify-end w-full 2xl:mb-[0.8vw] md:mb-3 mb-2">
          <button
            onClick={onClose}
            className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 flex-shrink-0"
          >
            <svg
              className="w-full h-full"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                fill="#333938"
              />
            </svg>
          </button>
        </div>

        {/* Lifeline Icon */}
        <div className="2xl:w-[5vw] 2xl:h-[5vw] xl:w-20 w-16 xl:h-20 h-16 flex items-center justify-center 2xl:mb-[1vw] mb-4 mx-auto">
          {lifelineImage && (
            <img
              src={lifelineImage}
              alt={lifelineName}
              className="w-full h-full object-contain mx-auto drop-shadow-lg"
            />
          )}
        </div>

        {/* Lifeline Name */}
        <h2 className="2xl:text-[1.35vw]/tight xl:text-2xl/tight text-xl/tight font-bold text-center text-black 2xl:mb-[0.4vw] mb-2 font-garamond">
          {lifelineName}
        </h2>

        {/* Team Name Badge */}
        <div className="flex justify-center 2xl:mb-[0.625vw] mb-3">
          <span className="inline-block 2xl:px-[0.833vw] px-3 2xl:py-[0.25vw] py-1 bg-primary-ivory lg:border-[0.13vw] border-2 border-transparent rounded-full text-black-300 2xl:text-[0.729vw] text-xs font-medium">
            {teamName}
          </span>
        </div>

        {/* Lifeline Description */}
        <p className="text-center text-gray-600 2xl:text-[1vw]/snug xl:text-lg/snug text-lg/snug 2xl:mb-[1vw] md:mb-5 mb-4 px-2 font-semibold">
          {lifelineDescription}
        </p>

        {/* Warning Message */}
        {/* <div className="text-center 2xl:mb-[1.25vw] md:mb-5 mb-4 2xl:py-[0.5vw] py-2 2xl:px-[0.833vw] px-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-amber-700 2xl:text-[0.729vw] text-xs font-medium">
            {t("lifelineConfirmModal.thisActionCannotBeUndone")}
          </p>
        </div> */}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <SecondaryBtn onClick={onClose} to="" className="xs:w-1/2 max-w-full lg:mb-0 md:mb-0 mb-0">
            {t("lifelineConfirmModal.cancel")}
          </SecondaryBtn>
          <PrimaryBtn onClick={() => onConfirm()} isLoading={isLoading} className="flex-1 max-w-full!">
            {t("lifelineConfirmModal.confirm")}
          </PrimaryBtn>
        </div>
      </div>

      <style>{`
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default LifelineConfirmModal;
